'use client';

import { Button, type ButtonVariant, type ButtonSize } from './Button';

export interface PaginationProps {
  page: number;
  total: number;
  limit: number;
  pages: number;
  onPageChange: (page: number) => void;
  size?: ButtonSize;
  variant?: ButtonVariant;
  showFirstLast?: boolean;
  className?: string;
}

function getVisiblePages(current: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];

  if (current <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push('ellipsis');
    pages.push(totalPages);
  } else if (current >= totalPages - 3) {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    pages.push('ellipsis');
    for (let i = current - 1; i <= current + 1; i++) pages.push(i);
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return pages;
}

export function Pagination({
  page,
  total,
  limit,
  pages,
  onPageChange,
  size = 'sm',
  variant = 'outline',
  showFirstLast = false,
  className = '',
}: PaginationProps) {
  if (pages <= 1) return null;

  const visiblePages = getVisiblePages(page, pages);
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <nav
      role="navigation"
      aria-label="Pagination"
      className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}
    >
      {/* Info */}
      <p className="text-sm text-(--muted)">
        Showing <span className="font-medium text-(--text)">{start}</span>–
        <span className="font-medium text-(--text)">{end}</span> of{' '}
        <span className="font-medium text-(--text)">{total}</span>
      </p>

      {/* Controls */}
      <div className="inline-flex items-center gap-1">
        {showFirstLast && (
          <Button
            variant={variant}
            size={size}
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            aria-label="First page"
          >
            «
          </Button>
        )}

        <Button
          variant={variant}
          size={size}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹
        </Button>

        {visiblePages.map((p) =>
          p === 'ellipsis' ? (
            <span
              key={`ellipsis-${Math.random()}`}
              className="px-2 text-(--muted)"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'primary' : variant}
              size={size}
              onClick={() => onPageChange(p)}
              aria-label={`Page ${p}`}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </Button>
          )
        )}

        <Button
          variant={variant}
          size={size}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pages}
          aria-label="Next page"
        >
          ›
        </Button>

        {showFirstLast && (
          <Button
            variant={variant}
            size={size}
            onClick={() => onPageChange(pages)}
            disabled={page >= pages}
            aria-label="Last page"
          >
            »
          </Button>
        )}
      </div>
    </nav>
  );
}

export default Pagination;
